const AI_SEARCH_BANNER_COOKIE = 'ai_search_banner_dismissed';
const BANNER_COOKIE_DURATION_DAYS = 365;

// Debounce utility
const debounce = (fn, delay) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), delay);
  };
};

// Track current device type for resize handling
let currentBannerDeviceType = null;

// Target elements for different screen sizes
const AI_SEARCH_BANNER_TARGETS = {
  desktop: '.gh-header__search.gh-desktop-only',  // > 1024px
  tablet: '.m-gh-header__ai-search',   // 768px - 1024px
  mobile: '#MainContent'    // <= 768px
};

// Device-specific styles
const AI_SEARCH_BANNER_STYLES = {
  desktop: {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    left: '496px',
    marginLeft: '16px'
  },
  tablet: {
    position: 'relative',
    top: 'auto',
    transform: 'none',
    left: 'auto',
    marginLeft: '0',
    minWidth: '282px'
  },
  mobile: {
    position: 'relative',
    top: 'auto',
    transform: 'none',
    left: 'auto',
    marginLeft: '0',
    marginTop: '12px',
    marginBottom: '40px',
    display: 'flex',
    marginRight: '12px',
    marginLeft: '12px',
    width: 'unset'
  }
};

const getDeviceType = () => {
  const width = window.innerWidth;
  if (width > 1024) return 'desktop';
  if (width >= 768) return 'tablet';
  return 'mobile';
};

const getTargetSelector = () => {
  const deviceType = getDeviceType();
  return AI_SEARCH_BANNER_TARGETS[deviceType];
};

const AI_SEARCH_BANNER_TRANSLATIONS = {
  en: 'Exclusive AI Search access 🎉',
  de: 'Exklusiver Zugriff auf die KI-Suche 🎉',
  fr: 'Accès exclusif à la recherche IA 🎉',
  es: 'Acceso exclusivo a la búsqueda con IA 🎉',
  'pt-BR': 'Acesso exclusivo à busca com IA 🎉'
};

const getAISearchBannerText = () => {
  const locale = Shopify.locale || 'en';
  return AI_SEARCH_BANNER_TRANSLATIONS[locale] || AI_SEARCH_BANNER_TRANSLATIONS.en;
};

/* VWO_DISABLED: Uncomment when VWO account is restored
const getVwoUUIDFromCookie = (accountId) => {
  const uuidCookie = getCookie('myfonts_custom_uuid');
  if(uuidCookie){
    return uuidCookie;
  }
  const uuid = vwoSdk.getUUID(getAnonymousID(), accountId);
  setCookie('myfonts_custom_uuid', uuid, 30); // 30 days
  localStorage.setItem('myfonts_custom_uuid', uuid);
  return uuid;
}
*/

// Check if banner should be shown (not dismissed)
const shouldShowAISearchBanner = () => {
  const dismissed = getCookie(AI_SEARCH_BANNER_COOKIE);
  return dismissed !== '1';
};

// Remove existing banner
const removeAISearchBanner = () => {
  const existingBanner = document.getElementById('ai-search-banner');
  if (existingBanner) {
    existingBanner.remove();
  }
  currentBannerDeviceType = null;
};

// Create and inject AI search banner
const injectAISearchBanner = () => {
  if (!shouldShowAISearchBanner()) {
    return;
  }

  const cookieDuration = BANNER_COOKIE_DURATION_DAYS;
  
  // Set cookie to 0 (banner shown but not dismissed)
  setCookie(AI_SEARCH_BANNER_COOKIE, '0', cookieDuration);

  const bannerHTML = `
    <div id="ai-search-banner" style="
      font-family: HelveticaNowMTMicroMedium, HelveticaNowMTMicro, sans-serif;
      background: linear-gradient(90deg, #86164B, #430B25);
      color: #fff;
      padding: 12px;
      display: inline-flex;
      align-items: center;
      gap: 12px;
      font-size: 11px;
      border-radius: 8px;
      width: fit-content;
      height: 40px;
    ">
      <div class="star-vector" style="position: absolute; top: 0; left: 0;">
        <svg width="14" height="13" viewBox="0 0 14 13" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path opacity="0.2" d="M-1.375 4.1C-1.75 4.24167 -2 4.6 -2 5C-2 5.4 -1.75 5.75833 -1.375 5.9L3.33333 7.66667L5.1 12.375C5.24167 12.75 5.6 13 6 13C6.4 13 6.75833 12.75 6.9 12.375L8.66667 7.66667L13.375 5.9C13.75 5.75833 14 5.4 14 5C14 4.6 13.75 4.24167 13.375 4.1L8.66667 2.33333L6.9 -2.375C6.75833 -2.75 6.4 -3 6 -3C5.6 -3 5.24167 -2.75 5.1 -2.375L3.33333 2.33333L-1.375 4.1Z" fill="#E6A3B9"/>
        </svg>
      </div>
      <div class="rect-vector" style="position: absolute; top: 0; left: 50%; transform: translateX(-50%);">
        <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect opacity="0.2" x="-0.828369" y="4.82861" width="8" height="8" rx="2" transform="rotate(-45 -0.828369 4.82861)" fill="#E6A3B9"/>
        </svg>
      </div>
      <div class="ai-search-banner-arrow" style="display: flex;">
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path fill-rule="evenodd" clip-rule="evenodd" d="M7.197 2.80317C7.48989 3.09606 7.48989 3.57093 7.197 3.86383L3.81066 7.25016H14C14.4142 7.25016 14.75 7.58595 14.75 8.00016C14.75 8.41438 14.4142 8.75016 14 8.75016H3.81066L7.197 12.1365C7.48989 12.4294 7.48989 12.9043 7.197 13.1972C6.9041 13.4901 6.42923 13.4901 6.13634 13.1972L1.46967 8.53049C1.17678 8.2376 1.17678 7.76273 1.46967 7.46983L6.13634 2.80317C6.42923 2.51027 6.9041 2.51027 7.197 2.80317Z" fill="#FCFCFD"/>
        </svg>
      </div>
      <span style="white-space: nowrap; display:flex; align-items: center;">
        ${getAISearchBannerText()}
        <span style="display: none;" class="mobile-arrow">
            <svg width="26" height="15" viewBox="0 0 26 15" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M25.5167 2.42914L24.0239 0.406436C23.6504 -0.0995334 22.9082 -0.139351 22.4828 0.323762L20.8788 2.0699L21.4683 2.61392L22.8086 1.1558C22.5461 4.35398 21.596 6.78071 20.2226 8.60631C18.6937 10.6387 16.6181 11.9553 14.3116 12.7531C9.82788 14.3041 4.51461 13.878 0.850473 12.9657L0.501141 12.8759L0.461053 12.8674C0.260668 12.8355 0.0653452 12.9603 0.0129948 13.1609C-0.0393227 13.3616 0.0699368 13.5672 0.260043 13.6388L0.299138 13.6511L0.657014 13.7432C4.41083 14.6782 9.89417 15.1303 14.5753 13.5111C16.999 12.6727 19.2193 11.2746 20.8623 9.09055C22.345 7.11969 23.3399 4.53252 23.609 1.19066L24.8741 2.90421L24.9003 2.93619C25.0369 3.0867 25.2672 3.11244 25.4336 2.98944C25.6111 2.85818 25.6483 2.60746 25.5167 2.42914ZM20.8788 2.0699C20.7296 2.23249 20.7402 2.48596 20.9029 2.63623C21.0657 2.78651 21.3189 2.77651 21.4683 2.61392L20.8788 2.0699Z" fill="white"/>
            </svg>
        </span>      
      </span>
      <button id="ai-search-banner-close" style="
        background: transparent;
        display: flex;
        border: none;
        color: #fff;
        cursor: pointer;
        padding: 4px 8px;
        line-height: 1;
      " aria-label="Close banner">
      <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path fill-rule="evenodd" clip-rule="evenodd" d="M2.18499 2.18499C2.43163 1.93834 2.83153 1.93834 3.07817 2.18499L6 5.10681L8.92183 2.18499C9.16847 1.93834 9.56837 1.93834 9.81502 2.18499C10.0617 2.43163 10.0617 2.83153 9.81502 3.07817L6.89319 6L9.81502 8.92183C10.0617 9.16847 10.0617 9.56837 9.81502 9.81502C9.56837 10.0617 9.16847 10.0617 8.92183 9.81502L6 6.89319L3.07817 9.81502C2.83153 10.0617 2.43163 10.0617 2.18499 9.81502C1.93834 9.56837 1.93834 9.16847 2.18499 8.92183L5.10681 6L2.18499 3.07817C1.93834 2.83153 1.93834 2.43163 2.18499 2.18499Z" fill="white"/>
      </svg>
      </button>
    </div>
  `;

  const waitForElement = (selector, callback) => {
    const el = document.querySelector(selector);
    if (el) {
      callback(el);
      return;
    }
    const observer = new MutationObserver((mutations, obs) => {
      const el = document.querySelector(selector);
      if (el) {
        obs.disconnect();
        callback(el);
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  };

  const deviceType = getDeviceType();
  const targetSelector = AI_SEARCH_BANNER_TARGETS[deviceType];
  if (!targetSelector) return;
  
  // Remove existing banner if device type changed
  if (currentBannerDeviceType && currentBannerDeviceType !== deviceType) {
    removeAISearchBanner();
  }
  
  // Skip if banner already exists for this device type
  if (document.getElementById('ai-search-banner')) return;

  waitForElement(targetSelector, (headerSearchEl) => {
    headerSearchEl.parentNode.style.position = 'relative';
    currentBannerDeviceType = deviceType;
    const bannerEl = document.createElement('div');
    bannerEl.innerHTML = bannerHTML;
    const banner = bannerEl.firstElementChild;
    banner.classList.add(`ai-search-banner--${deviceType}`);
    
    // Apply device-specific styles
    const deviceStyles = AI_SEARCH_BANNER_STYLES[deviceType];
    if (deviceStyles) {
      Object.assign(banner.style, deviceStyles);
    }
    
    // Insert: first child for mobile, before for tablet, after for desktop
    if (deviceType === 'mobile') {
      headerSearchEl.prepend(banner);
    } else {
      const insertPosition = deviceType === 'tablet' ? 'beforebegin' : 'afterend';
      headerSearchEl.insertAdjacentElement(insertPosition, banner);
    }
    
    // Move arrow to the end and rotate for tablet
    if (deviceType === 'tablet') {
      const arrowWrapper = banner.querySelector('.ai-search-banner-arrow');
      const closeBtn = banner.querySelector('button');
      const bannerTextEl = banner.querySelector('span');

      if (arrowWrapper) {
        arrowWrapper.style.order = '2';
        arrowWrapper.style.transform = 'rotate(180deg)';
        arrowWrapper.style.borderRight = '1px solid #86164B';
        arrowWrapper.style.position = 'absolute';
        arrowWrapper.style.right = '12px';
        arrowWrapper.style.paddingRight = '8px';
      }
      if (closeBtn) {
        closeBtn.style.order = '3';
        closeBtn.style.position = 'absolute';
        closeBtn.style.right = '36px';
      }
      if (bannerTextEl) {
        bannerTextEl.style.marginRight = '60px';
      }
    }
    
    // Hide normal arrow and show mobile arrow for mobile
    if (deviceType === 'mobile') {
      const arrowWrapper = banner.querySelector('.ai-search-banner-arrow');
      const closeBtn = banner.querySelector('button');
      const mobileArrow = banner.querySelector('.mobile-arrow');
      if (arrowWrapper) arrowWrapper.style.display = 'none';
      if (mobileArrow){
        mobileArrow.style.display = 'inline';
        mobileArrow.style.marginLeft = '6px';
      }
      if (closeBtn) {
        closeBtn.style.position = 'absolute';
        closeBtn.style.right = '12px';
      }
    }

    
    // Add close handler after banner is injected
    const closeBtn = document.getElementById('ai-search-banner-close');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => {
        const banner = document.getElementById('ai-search-banner');
        if (banner) {
          banner.remove();
        }
        // Update cookie to 1 (dismissed)
        setCookie(AI_SEARCH_BANNER_COOKIE, '1', BANNER_COOKIE_DURATION_DAYS);
      });

      // Hover effect
      closeBtn.addEventListener('mouseenter', () => { closeBtn.style.opacity = '1'; });
      closeBtn.addEventListener('mouseleave', () => { closeBtn.style.opacity = '0.8'; });
    }
  });
};

// Handle resize with debounce
const handleBannerResize = debounce(() => {
  const newDeviceType = getDeviceType();
  if (currentBannerDeviceType && currentBannerDeviceType !== newDeviceType) {
    removeAISearchBanner();
    injectAISearchBanner();
  }
}, 250);

// Setup resize listener
const setupBannerResizeHandler = () => {
  window.addEventListener('resize', handleBannerResize);
};

/* VWO_DISABLED: Uncomment when VWO account is restored
async function vwoFeatureFlagEnabler(accountId, sdkKey){
    try{
        const queryParams = new URLSearchParams(window.location.search);
        const experimentalAiSearch = queryParams.get('experimental_ai_search');
        const options = {
          accountId: accountId,
          sdkKey: sdkKey 
        };
        const vwoClient = await vwoSdk.init(options);
        const userContext = {
          id: getVwoUUIDFromCookie(accountId)
        }
        const flag = await vwoClient.getFlag("aiSearch", userContext);
        const isFeatureFlagEnabled = flag.isEnabled();
        const aiSearchVal = flag.getVariable("enableAISearch");
        const aiOrchestratorVal = flag.getVariable("enableOrchestrator");
        const el = document.getElementsByClassName("ai_search_dotlottie_wrapper")[0];
        console.log("is feature flag enabled", isFeatureFlagEnabled, userContext);
        console.log("All vars [enableAISearch, enableOrchestrator]", aiSearchVal, aiOrchestratorVal);

        if(isFeatureFlagEnabled || experimentalAiSearch === 'x_ai_true'){
          gs_settings.config.contextualSearch.isEnabled = true;
          if(aiOrchestratorVal){
            const isProd = document.location.hostname.includes('myfonts.com')??false;
            gs_settings.config.contextualSearch.orchestrateBaseUrl = `https://${isProd ? 'api' : 'pp-api'}.monotype.com/v1/fontgpt`;
          }
          loadSearchBar();
          
          // Inject AI search banner
          injectAISearchBanner();
          setupBannerResizeHandler();
          
          if(window.location.pathname.includes("ai-search")){
            const slideWrapper = document.getElementsByClassName("gh-sliderWrapper")[0];
            if (slideWrapper && slideWrapper.style) {
              slideWrapper.style.display = 'none';
            }
          }
        }else {
          if(window.location.pathname.includes("ai-search")){
            const searchPageUrl = myfonts.locale === 'en' ? '/search' : `/${myfonts.locale}/search`;
            window.location.href = searchPageUrl;
          }
          if(el){
            el.remove();
          }else{
            if(typeof searchPageData !== 'undefined'){
              searchPageData.config.contextualSearch.isEnabled = false;
            }
            
          }
        }
      } catch (e) {
        console.error(e);
      }
}
*/

async function aiSearchGeoEnabler() {
  try {
    const serviceHost = monotype_services_app;
    const geoApiUrl = `${serviceHost}/ipinfo`;
    

    let rawCountries = window.__aiSearchAllowedCountries || [];
    if (typeof rawCountries === 'string') {
      try { rawCountries = JSON.parse(rawCountries); } catch (_) { rawCountries = []; }
    }
    const allowedCodes = Array.isArray(rawCountries)
      ? rawCountries.map(c => (typeof c === 'string' ? c : c.code || '')).filter(Boolean).map(c => c.toUpperCase())
      : [];
    console.log('allowedCodes', allowedCodes);

    const queryParams = new URLSearchParams(window.location.search);
    const experimentalAiSearch = queryParams.get('experimental_ai_search');

    let shouldEnable = false;

    if (experimentalAiSearch === 'x_ai_true') {
      shouldEnable = true;
    } else if (allowedCodes.length === 0) {
      shouldEnable = true;
    } else {
      try {
        const cached = sessionStorage.getItem('ai_search_country_code');
        if (cached) {
          console.log('[AI Search] Using cached country:', cached);
          shouldEnable = allowedCodes.includes(cached);
        } else {
          const response = await fetch(geoApiUrl, { method: 'POST', headers: { 'Content-Type': 'application/json' } });
          const data = await response.json();
          const countryCode = (data.country_code || '').toUpperCase();
          console.log('[AI Search] Detected country:', countryCode, data);
          sessionStorage.setItem('ai_search_country_code', countryCode);
          shouldEnable = allowedCodes.includes(countryCode);
        }
      } catch (geoErr) {
        console.error('[AI Search] Geo-IP lookup failed, enabling as fallback', geoErr);
        shouldEnable = false;
      }
    }

    console.log('[AI Search] shouldEnable:', shouldEnable);

    const el = document.getElementsByClassName("ai_search_dotlottie_wrapper")[0];

    if (shouldEnable) {
      gs_settings.config.contextualSearch.isEnabled = true;
      gs_settings.config.contextualSearch.orchestrateBaseUrl = `https://${serviceHost.includes('stage') ? 'pp-api' : 'api'}.monotype.com/v1/fontgpt`;
      loadSearchBar();

      injectAISearchBanner();
      setupBannerResizeHandler();

      if (window.location.pathname.includes("ai-search")) {
        const slideWrapper = document.getElementsByClassName("gh-sliderWrapper")[0];
        if (slideWrapper && slideWrapper.style) {
          slideWrapper.style.display = 'none';
        }
      }
    } else {
      if (window.location.pathname.includes("ai-search")) {
        const searchPageUrl = myfonts.locale === 'en' ? '/search' : `/${myfonts.locale}/search`;
        window.location.href = searchPageUrl;
      }
      if (el) {
        el.remove();
      } else {
        if (typeof searchPageData !== 'undefined') {
          searchPageData.config.contextualSearch.isEnabled = false;
        }
      }
    }
  } catch (e) {
    console.error(e);
  }
}